import { useMemo } from "react";
import { useSelector } from "react-redux";
import { enums } from "../lib";
import usePendingCountWarningEnabled from "./use-pending-count-warning-enabled";

/**
 * Use Pending Count Progress Hook
 *
 * While an audit is in Pending Count the auditor has recorded assets on their
 * device but has not submitted the audit, so the images and notes behind those
 * records have not reached the controller yet.
 *
 * Reports how far the count has got. Returns null when the audit is not in
 * Pending Count, or when the organisation has switched the warning off, so
 * callers render nothing and every other case is untouched.
 *
 * Counts only the assets that actually have to be reconciled — expected and
 * mandatory, same definition the Review page's own "Expected" bucket uses
 * (see `useExpectedAssetColsRows.js` / `audit-review/saga.js`). Non-mandatory
 * and shared-validation assets don't block the count, so including them here
 * inflates the total and understates progress — an audit with 8 assets that
 * actually need counting and 43 non-mandatory ones would otherwise show
 * "0 of 51 counted" instead of "0 of 8".
 */
export default () => {
    const { auditsList, auditAssets } = useSelector((state) => state);
    const warningEnabled = usePendingCountWarningEnabled();

    return useMemo(() => {
        if (!warningEnabled) return null;

        const status = auditsList?.item?.status;
        if (status !== enums.status.audit.pendingCount) return null;

        const assets = (auditAssets?.data ?? []).filter(
            (asset) =>
                !asset.sharedValidationAsset &&
                asset.isExpected === true &&
                asset.isMandatory === true &&
                asset.isRelated === true
        );
        if (assets.length === 0) return null;

        return {
            counted: assets.filter((asset) => asset.isAssigned).length,
            total: assets.length
        };
    }, [auditsList, auditAssets, warningEnabled]);
};
